import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet-async";

import FlashOnIcon from "@mui/icons-material/FlashOn";
import VerifiedIcon from "@mui/icons-material/Verified";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BuildIcon from "@mui/icons-material/Build";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import GroupsIcon from "@mui/icons-material/Groups";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import image from "../Assets/Images/user.png"
import ContactBookingSection from "../ContactUsPage/ContactBookingSection";

const cardHover = {
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
  },
};

const WaterDamageLanding = () => {
  return (
    <>
      {/* ================= SEO ================= */}
      <Helmet>
        <title>
          Water Damage Restoration Bloomington IN | 24/7 Emergency | Rite Water
        </title>
        <meta
          name="description"
          content="24/7 emergency water damage restoration in Bloomington, Indiana. Water extraction, drying, mold prevention & insurance support."
        />
        <link
          rel="canonical"
          href="https://ritewaterrestoration.com/water-damage-help"
        />
      </Helmet>

      <Box width="100%" bgcolor="#f7f4ef">

        {/* ================= HERO ================= */}
        <Box py={{ xs: 4, md: 6 }}>
          <Container>
            <Grid container spacing={4} alignItems="center">

              <Grid item xs={12} md={7}>
                <Stack spacing={2}>
                  <Chip
                    icon={<FlashOnIcon />}
                    label="24/7 Emergency Service"
                    sx={{
                      bgcolor: "#ff8a00",
                      color: "#fff",
                      fontWeight: 700,
                      width: "fit-content",
                    }}
                  />

                  <Typography fontWeight={800} sx={{ fontSize: { xs: 30, md: 44 } }}>
                    Expert Water Damage Restoration in Bloomington, IN
                  </Typography>

                  <Typography color="text.secondary">
                    Rapid water extraction, structural drying, mold prevention,
                    and full restoration for homes and businesses.
                  </Typography>

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <VerifiedIcon sx={{ color: "#2ca24b" }} />
                      <Typography>IICRC Certified Technicians</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BuildIcon sx={{ color: "#2ca24b" }} />
                      <Typography>Advanced Drying & Detection Equipment</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GroupsIcon sx={{ color: "#2ca24b" }} />
                      <Typography>Local & Family Focused Team</Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  component="img"
                  src={image}
                  alt="Emergency Water Damage Restoration"
                  sx={{ width: "100%", borderRadius: 3 }}
                />
              </Grid>

            </Grid>
          </Container>
        </Box>

        {/* ================= FORM ================= */}
        <Box py={4} bgcolor="#f1ede4">
          <Container>
            <Card sx={{ borderRadius: 3, p: 3 }} elevation={3}>
              <Typography fontWeight={800} mb={2}>
                Request a Free Inspection
              </Typography>

              <Box
                sx={{
                  "& form": {
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(6, 1fr)" },
                    gap: 2,
                  },
                }}
              >
                <ContactBookingSection />
              </Box>
            </Card>
          </Container>
        </Box>

        {/* ================= SERVICES ================= */}
        <Box py={6} bgcolor="#ffffff">
          <Container>
            <Typography fontWeight={800} textAlign="center" mb={4}>
              Comprehensive Restoration Services
            </Typography>

            <Grid container spacing={3}>
              {[
                { title: "Water Extraction & Drying", icon: <WaterDropIcon /> },
                { title: "Mold Prevention & Remediation", icon: <BuildIcon /> },
                { title: "Sewage Cleanup (Black Water)", icon: <HomeRepairServiceIcon /> },
                { title: "Drywall & Flooring Repair", icon: <BuildIcon /> },
              ].map((item, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card
                    sx={{
                      ...cardHover,
                      height: "100%",
                      borderRadius: 3,
                      bgcolor: "#f7f4ef",
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1} alignItems="center">
                        <Box sx={{ color: "#ff8a00" }}>{item.icon}</Box>
                        <Typography fontWeight={700} textAlign="center">
                          {item.title}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ================= PROCESS ================= */}
        <Box py={6} bgcolor="#f1ede4">
          <Container>
            <Typography fontWeight={800} textAlign="center" mb={4}>
              Our 4-Step Restoration Process
            </Typography>

            <Grid container spacing={3}>
              {[
                "Inspection & Assessment",
                "Water Removal",
                "Drying & Dehumidification",
                "Restoration & Repair",
              ].map((step, i) => (
                <Grid item xs={12} md={3} key={i}>
                  <Card sx={{ ...cardHover, height: "100%", borderRadius: 3 }}>
                    <CardContent>
                      <Typography
                        fontWeight={800}
                        sx={{ color: "#2ca24b" }}
                      >
                        Step {i + 1}
                      </Typography>
                      <Typography fontWeight={600}>{step}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ================= CONTACT INFO ================= */}
        <Box py={6} bgcolor="#ffffff">
          <Container>
            <Grid container spacing={4}>

              <Grid item xs={12} md={6}>
                <Typography fontWeight={800} mb={2}>
                  Customer Support
                </Typography>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon sx={{ color: "#ff8a00" }} />
                    <Typography fontWeight={600}>
                      +1 (888) 835-2473
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon sx={{ color: "#ff8a00" }} />
                    <Typography>info@ritewaterrestoration.com</Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOnIcon sx={{ color: "#ff8a00" }} />
                    <Typography>
                      Rite Water Restoration, Bloomington, Indiana
                    </Typography>
                  </Stack>

                  <Typography color="text.secondary">
                    Serving Bloomington & Nearby Areas
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography fontWeight={800} mb={2}>
                  Connect With Us
                </Typography>

                <Stack direction="row" spacing={2}>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/ritewaterrestoration/"
                    target="_blank"
                    sx={{ bgcolor: "#ff8a00", color: "#fff", "&:hover": { bgcolor: "#2ca24b" } }}
                  >
                    <InstagramIcon />
                  </IconButton>

                  <IconButton
                    component="a"
                    href="https://www.facebook.com/ritewaterrestoration"
                    target="_blank"
                    sx={{ bgcolor: "#ff8a00", color: "#fff", "&:hover": { bgcolor: "#2ca24b" } }}
                  >
                    <FacebookIcon />
                  </IconButton>
                </Stack>
              </Grid>

            </Grid>
          </Container>
        </Box>

        {/* ================= FOOTER CTA ================= */}
        <Box py={4} bgcolor="#2ca24b">
          <Container>
            <Typography color="#fff" fontWeight={800} textAlign="center">
              Call Us Anytime — We’re Available 24 Hours a Day
            </Typography>
          </Container>
        </Box>

      </Box>
    </>
  );
};

export default WaterDamageLanding;
